import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './licence'
import './index.css'

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')
const mount = root

function showFatal(title: string, message: string): void {
  mount.innerHTML = `
    <main class="centered">
      <h1>${title}</h1>
      <p class="error">${message.replace(/</g, '&lt;')}</p>
    </main>`
}

/**
 * auth.ts validates its configuration at module scope and throws if an Entra
 * identifier is missing. A static import would make that a load-time failure:
 * blank page, reason only in the console — the worst possible outcome for a
 * misconfigured deployment.
 *
 * Importing dynamically makes the error catchable, so the page says what is
 * actually wrong.
 *
 * Wrapped in an async function rather than using top-level await, which needs
 * a browser target of ES2022 or later. Vite's default target rejects it at
 * build time — and the dev server transpiles differently, so it builds and
 * runs locally right up until CI fails.
 */
async function start(): Promise<void> {
  const [{ msal, msalReady }, { MsalProvider }, { default: App }] = await Promise.all([
    import('./auth'),
    import('@azure/msal-react'),
    import('./App'),
  ])

  // MSAL must finish initialising before React renders, or the first
  // useMsal() call races it.
  await msalReady

  createRoot(mount).render(
    <StrictMode>
      <MsalProvider instance={msal}>
        <App />
      </MsalProvider>
    </StrictMode>,
  )
}

void start().catch((err: Error) => showFatal('Configuration incomplete', err.message))
